import { IUserProfile } from './iprofile';
import { IRole } from './irole';
import { IUser } from './iuser';
import { IBrand } from './ibrand';
import { MainModules, MenuType } from 'common/constants/features';
import { FunctionComponent, SVGProps } from 'react';
import { SnackBarAlertType, SnackBarIconType } from 'common/constants';
import { extend } from 'lodash';

export interface BaseState {
  loading: boolean;
  error: UIError | null | undefined;
}

export interface LogMsgType {
  '@timestamp': string;
  category: string;
  message: string;
  requestId: string;
  appVersion: string;
  interactionId: string;
  level: string;
  userId: number;
  sessionId: string | null;
  tag: string | null;
}

export enum SnackbarType {
  INFO = 1,
  SUCCESS = 2,
  WARNING = 3,
  ERROR = 4,
}

export interface AppModal {
  id: string;
  name?: string;
}

export interface AppConfig {
  appVersionId: string;
  appVersionCode: string;
  localInfo: LocaleInfo;
  defaultListSize: number;
  maxUploadableFileSize: number;
  cdnUrl: string;
}

export interface AppState extends BaseState {
  isAppInitialized: boolean;
  isUserLoggedIn: boolean;
  loggedInUser: IUserProfile | null;
  snackBarStates: iSnackBar;
  appLoaderState: AppLoaderState | null;
  appConfig: AppConfig;
}

export interface AsyncStorageData {
  expiryTime: number; //long in millis
  data: any;
}

export interface AppLoaderState {
  visible: boolean;
  disableBack?: boolean;
  message?: string;
}

export enum ModalType {
  LOCATION_BANNED = 'locationBanned',
  APP_UPDATE = 'appUpdateModal',
  ACTION_RESTRICETED = 'restrictedModal',
}

export interface Avatar {
  id: number;
  isCustomAvatar: boolean;
  url: string;
  uri: string;
}

export interface LocaleInfo {
  currency: string;
  currencySymbol: string;
}

export interface UIError {
  msg: string;
  code: string;
}

export interface IMoreInfo {
  remainingAmount?: number;
}

export interface IPageInfo {
  totalPages: number;
  currentPage: number;
  totalItems: number;
}

export interface IPageData<T> {
  totalPages: number;
  currentPage: number;
  totalItems: number;
  items: T[];
}

export interface ServerResponse {
  status: {
    success: boolean;
    msg: string;
    code?: string;
  };
  error?: UIError | null;
}

export interface PagedDataResponse<T> extends ServerResponse {
  data: IPageData<T>;
}

export interface AuthState {
  loading: boolean;
  loginPayload: LoginPayload;
  loggedInUser: IUser | null;
  otpRequested: boolean;
  usernameUpdated: boolean;
  authTkn: string | null;
  error: UIError | null;
  requestOtpError: UIError | null;
  passwordResetDone: boolean | null;
}

export interface AuthResp extends ServerResponse {
  data: {
    user: IUser | null;
    token: string;
    role: IRole;
    brands: IBrand[] | null;
  };
}

export interface CheckUserResp extends ServerResponse {
  data: {
    isNewUser: boolean;
  };
}

export interface ValidateReferralResp extends ServerResponse {
  data: {
    referralCode: string;
    valid: boolean;
    message: string;
  };
}

export interface Language {
  name: string;
  key: string;
  letters?: string;
}
export interface AppLanguageResp extends ServerResponse {
  data: Array<Language>;
}
export interface AppLocaleInfoResp extends ServerResponse {
  data: LocaleInfo;
}

export interface AppConfigResp extends ServerResponse {
  data: AppConfig;
}

export interface LoginPayload {
  username: string;
  password: string;
  token?: string;
  confirmPassword?: string;
}

export interface ChangePasswordPayload {
  password: string;
  confirmPassword: string;
  currentPassword: string;
}

export interface IDeviceData {
  'btl-android-version': string;
  'btl-device-id': string;
  'btl-device-name': string;
  'btl-android-id': string;
  'btl-wifi-mac': string;
  'btl-app-version-name': string;
  'btl-app-bundle-name': string;
}

export interface IUpdateDetail {
  versionName: string;
  versionCode: number;
  releaseNotes?: Array<string>;
  releaseNotesUrl?: string;
  isMandatoryUpdate: boolean;
  downloadUrl: string;
}

export interface IDeviceInformation {
  brand: string;
  buildNumber: string;
  deviceId: string;
  manufacturer: string;
  systemVersion: string;
  deviceTotalMemory: number;
  phoneNumber?: string;
  deviceName: string;
  deviceOS: string;
}

export enum CTAType {
  REDIRECT = 'REDIRECT',
  NAVIGATE = 'NAVIGATE',
}

export interface ResourceBundle {
  [key: string]: string;
}

export interface ResourceBundleResp extends ServerResponse {
  data: ResourceBundle;
}

export interface IEventData {
  eventAuthSuccess: boolean;
  failedRuleName: string;
  meta: any;
}

export interface ISearchCriteria {
  term: string;
}

export interface IFeature {
  logo: string | FunctionComponent<SVGProps<SVGSVGElement>>;
  title: string;
  type: MainModules;
}

export interface IMenuItem {
  logo: string | FunctionComponent<SVGProps<SVGSVGElement>>;
  title: string;
  type: MenuType;
}

export interface IDateFilter {
  startDate: string;
  endDate: string;
}

export type IServerResponse<T> = ServerResponse & { data?: T };

export interface GeneralAppConfig {
  defaultListSize: number;
  maxUploadableFileSize: number;
  cdnUrl: string;
  adminPwdRegex: string;
  userPwdRegex: string;
}

export type AppConfigResponse = IServerResponse<{
  updateDetails?: IUpdateDetail;
  generalConfig?: GeneralAppConfig;
}>;

export interface GetAppConfigQueryParams {
  appUpdateConfig?: boolean;
  generalConfig?: boolean;
}

export interface iSnackBar {
  visible: boolean;
  message: string;
  type: SnackbarType;
  showTime: number;
  iconType: SnackBarIconType | undefined;
  autoCloseDisabled?: boolean;
  disableClose?: boolean;
  alertType?: SnackBarAlertType;
}

export type ILocalStorageType = 'localStorage' | 'sessionStorage';

export type UIFormElementType =
  | 'textInput'
  | 'dropdown'
  | 'multiSelect'
  | 'imageUpload'
  | 'textList'
  | 'documents'
  | 'salesAttachmentUpload'
  | 'dynamicRows'
  | 'imageView'
  | 'timePicker'
  | 'label';

export type InputModeOptions = 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';

export type FormItem = {
  label?: string;
  type?: UIFormElementType;
  itemKey: string;
  inputMode?: InputModeOptions | undefined;
  validationType?: InputModeOptions | 'time';
  placeHolder?: string;
  value?: string | number | undefined;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  hideLabel?: boolean;
  objecType?: any;
  disabled?: boolean;
};

export type ErrorType = {
  [name: string]: string | undefined;
};

export interface IValidation {
  valid: boolean;
  error?: ErrorType;
}

export interface IDayReportField {
  id: number;

  enterpriseId: number;
  storeId: number;

  fieldName: string;
  fieldDisplayName: string;
  fieldOrder: number;

  categoryName: string;
  categoryDisplayName: string;
  categoryOrder: number;

  editable: boolean;
  version: number;

  formula: string;
  formulaVariables: string;
}

export interface IFieldCategory {
  categoryName: string;
  categoryDisplayName: string;
  categoryOrder: number;
}

export interface IDayReportCategoryWiseFields {
  fields: { [category: string]: IDayReportField[] };
  categories: IFieldCategory[];
}

export interface DayReportFieldsResponse extends IServerResponse<IDayReportCategoryWiseFields> {}

export interface IRoyaltyConfig {
  enterpriseId: number;
  salesCashF1: number;
  lotterySalesF1: number;
  lotterySalesF2: number;
  lotteryBonusF1: number;
  lottoCommissionF1: number;
}

export interface IRoyaltyConfigResponse extends IServerResponse<IRoyaltyConfig> {}

export type UIChipColor = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
